// ─── STORAGE & SERIALIZATION — pure functions, no React dependencies ──────────
// Supabase row mapping for bet objects. localStorage persistence removed (v1.0.242).
import * as Sentry from "./errorReporting";

// Retained for the pre-commit version-sync hook, which rewrites "const APP_VERSION"
// in this file (Hotspot #11). No longer consumed here after the dormant
// betToRow/legToRow serializers were removed (#799) — do not delete without
// also updating .githooks/pre-commit.
const APP_VERSION = "1.0.1908";

// const STORAGE_KEY = "sliptrack_bets_v1"; // unused — localStorage removed v1.0.242

// ── SUPABASE BET ROW MAPPING ──────────────────────────────────────────────────
// betToRow / legToRow (and their helpers marketForStorage /
// hasBinaryRawMarketSignal) were removed in #799. The client has no bet/leg
// write path — localStorage persistence was removed v1.0.242 and RLS blocks
// direct client bets/bet_legs inserts. All active writes go through
// api/import-confirm.js + api/lib/slipsync/handler.js, which normalize `pick`
// via api/lib/normalize-leg-pick.js. These dormant serializers wrote `pick`
// raw, bypassing that boundary — a latent #789 fail-closed defect if ever
// reactivated. Confirmed callerless (only tests referenced them) before removal.
// The read path (rowToBet below) remains active.

// The leg read (fetchLegRows: one unpaged IN-list GET that swallowed errors) was removed in #1554.
// The bet load is src/lib/bet-load.js: bets with bet_legs embedded, ordered by leg_index, paged.

// READ-PATH CONTRACT (#1052). bets.legs is a CLIENT-AUTHORED copy: sanitizeLegsForStorage
// (api/import-confirm.js) reconciles pick and line for EVERY league (via storedPickFor /
// storedLineFor, the same owners buildLegRows uses for bet_legs.{pick,line}) and rewrites
// market/type/canonicalMarket for server-authoritative leagues (NFL) and, since #946, for
// MLB legs the import gate classified fail-closed/unknown, spreading the
// rest of the client leg verbatim -- and nothing keeps it in sync afterwards --
// the correction cron rewrites bet_legs.game_id, the settle RPCs write leg_status (as
// does the slipsync import server-side, from the sportsbook's own settled result), and
// poll-live-stats writes frozen_stat/stat_value/live_context, all on bet_legs only. So
// bet_legs rows are the ONLY source a client-graded leg may be built from:
//
//   rows present (>= 1)  -> every leg comes from its row; bets.legs is NOT read.
//                            A null bet_legs.market (the ratified representation for an
//                            unsupported market) yields a null-market leg, which
//                            calcStatus grades `unresolvable` -- the server's own answer.
//   no rows (legacy)      -> bets.legs is read through JSONB_DESCRIPTIVE_KEYS only and
//                            every grading-relevant key is null. Renders, never grades.
//
// The previous rule was all-or-nothing (rows used only when EVERY row had a market) with
// a deny-list strip on the fallback; one null-market row dropped the whole bet onto the
// client copy, and anything not on the deny-list -- canonicalMarket, line, overUnder --
// still chose the calcStatus branch, so `{canonicalMarket:'ou', line:-1}` graded WON on
// score math alone. A deny-list cannot close that; only not reading the copy can.
//
// Adding a key here requires proof that calcStatus / marketKey (markets.js) /
// gradingLeague / hydrateSingleLeg never read it. Keys are copied by name -- never spread.
const JSONB_DESCRIPTIVE_KEYS = ['teams', 'pick', 'line', 'overUnder', 'odds', 'player', 'playerName', 'fullName'];

// LEAGUE is an authority field, not a descriptive one: calcStatus and
// isSettlementPendingForBet both key their settleability gate on it. The client
// supplies leg.league at import and NOTHING validates or overwrites it -- the request
// gate validates game IDs against the ROOT league only (import-confirm.js:699).
//
// Never-reject r4 R2: an UNTRACKED leg (tracking_status 'not_tracked') legitimately keeps
// its real league in bet_legs.league; the stamp below still applies, and the raw value
// survives as sourceLeague. Everything that follows is about TRACKED legs.
// Bets are SINGLE-LEAGUE by construction, so the root IS the per-leg league:
// import-confirm derives one league via classifyLeague (:1179) and buildLegRows stamps
// it on every row (:585); slipsync rejects mixed slips outright (handler.js:192).
// A per-leg league disagreeing with the root is therefore corrupt or spoofed, never
// legitimate -- so stamping the root over it loses nothing and closes the bypass for
// every consumer at once, not just the one that was exploited.
//
// The root is stamped UNCONDITIONALLY -- a null root yields a null leg league, which
// calcStatus grades `unresolvable` and isSettlementPendingForBet treats as not pending.
// No current writer produces a null-root bet (both write paths make league mandatory;
// bets.league carried DEFAULT 'nhl' until 2026-06-08), and the cron already skips such
// rows as missing_leg_league -- so failing closed here costs nothing and means a future
// writer that forgets league fails visibly instead of trusting the client.
// Build one client leg from one bet_legs row -- the only mapper on the rows-present path.
function legFromRow(r, rootLeague) {
  return {
    gameId:     r.game_id != null ? (Number(r.game_id) || r.game_id) : null,
    // Root-stamped, not r.league (#1037 round 3 P1): bet_legs.league is server-
    // written from the same value as bets.league, so a disagreement is corrupt and
    // must not choose the grading path. Null root -> null, never a default. The
    // shared league authority (gradingLeague) still compares against the game.
    league:     rootLeague,
    // The RAW bet_legs.league rides along as a SIGNAL for the league authority
    // (gradingLeague). Stamping it over `league` erased it before the authority ran
    // (Security r4 P2-1): root nhl / legs nfl / game nhl refused on the server and
    // graded on the client. Renderers keep using `league`; only the authority reads
    // this. Never persisted -- the client has no leg serializer (#799).
    // Withheld under a null root: a null root must never let a leg manufacture
    // settlement authority on the client (ratified contract; the cron differs for
    // server-stamped rows -- accepted display-only asymmetry, no writer produces it).
    sourceLeague: rootLeague == null ? null : (r.league ?? null),
    teams:      r.teams ?? null,
    pick:       r.pick ?? null,
    market:        r.market ?? null,
    type:          r.market ?? null,        // renderer alias: leg.type === leg.market
    canonicalMarket: r.market ?? null,
    line:       r.line ?? null,
    odds:       r.odds ?? null,
    overUnder:  r.over_under ?? null,
    player:     r.player_name ?? null,   // renderer alias: leg.player === leg.playerName
    playerName: r.player_name ?? null,
    statType:   r.stat_type ?? null,
    statValue:  r.stat_value ?? null,
    frozenStat: r.frozen_stat ?? null,
    frozenDnp: r.frozen_dnp === true ? true : null, // #1353 PR 3b: server-written (trigger-guarded), cron-owned
    status:     r.leg_status ?? null,    // the ONLY source of leg.status (#790 push chip); server-written
    liveContext: r.live_context ?? null,
    fullName:    r.full_name ?? null,
    // Never-reject PR 3: server-written tracking columns (trigger-guarded, CC-IMPL-007).
    // null = tracked (historic rows). isUntrackedLeg (betting.js) reads trackingStatus.
    trackingStatus:   r.tracking_status ?? null,
    untrackedReason:  r.untracked_reason ?? null,
    rawMarketText:    r.raw_market_text ?? null,
    rawSelectionText: r.raw_selection_text ?? null,
  };
}

// No-rows fallback. Same key set as legFromRow so renderers see one shape; every
// grading-relevant key is null by construction and only JSONB_DESCRIPTIVE_KEYS are
// copied in. `sourceLeague` is withheld (null): a client-authored league is never a
// settlement-authority signal.
function legFromJsonbFailClosed(leg, rootLeague) {
  const out = {
    gameId: null, league: rootLeague, sourceLeague: null,
    teams: null, pick: null, market: null, type: null, canonicalMarket: null,
    line: null, odds: null, overUnder: null, player: null, playerName: null,
    statType: null, statValue: null, frozenStat: null, frozenDnp: null, status: null,
    liveContext: null, fullName: null,
    // never from client JSONB: a client-authored leg is never a tracking signal
    trackingStatus: null, untrackedReason: null, rawMarketText: null, rawSelectionText: null,
  };
  if (leg && typeof leg === 'object' && !Array.isArray(leg)) {
    for (const k of JSONB_DESCRIPTIVE_KEYS) {
      if (!Object.prototype.hasOwnProperty.call(leg, k)) continue;
      // Scalars only (pre-Codex P2): import-confirm validates leg SHAPE, not TYPES, so an
      // object-valued `pick` reaches the renderer as a React child and unmounts the tree.
      const v = leg[k];
      if (typeof v === 'string' || typeof v === 'number') out[k] = v;
    }
  }
  const name = out.playerName ?? out.player ?? null;
  out.playerName = name;
  out.player = name;
  return out;
}

// bets.legs arrives as a JSON string on some PostgREST paths and as a parsed array on
// others. Anything that is not an array after parsing is treated as absent (null) --
// the previous code returned a non-array value unchanged.
function parseJsonbLegs(raw) {
  let value = raw;
  if (typeof value === 'string') {
    try { value = JSON.parse(value); } catch { return null; }
  }
  return Array.isArray(value) ? value : null;
}

export function rowToBet(row, legRows = null) {
  if (!row?.id) {
    throw new Error(`rowToBet: invalid row — missing id (row: ${JSON.stringify(row)})`);
  }

  const rootLeague = row.league ?? null;
  let legs;
  if (Array.isArray(legRows) && legRows.length > 0) {
    legs = legRows.map((r) => legFromRow(r, rootLeague));
  } else {
    const jsonb = parseJsonbLegs(row.legs);
    legs = jsonb == null ? null : jsonb.map((leg) => legFromJsonbFailClosed(leg, rootLeague));
  }

  return {
    id: Number(row.id) || row.id,
    slipType: row.slip_type,
    sportsbook: row.sportsbook,
    stake: row.stake,
    odds: row.odds,
    combinedOdds: row.combined_odds,
    pick: row.pick,
    teams: row.teams,
    line: row.line,
    overUnder: row.over_under,
    finalResult: row.final_result,
    autoSettled: row.auto_settled,
    legs,
    gameId: row.game_id != null ? (Number(row.game_id) || row.game_id) : null,
    importLogId: row.import_log_id ?? null,
    confirmedParseId: row.confirmed_parse_id ?? null,
    provenanceStatus: row.provenance_status ?? 'not_applicable',
    league: row.league ?? null,
    settledAt: row.settled_at ? new Date(row.settled_at).getTime() : null,
  };
}
